import React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import SensitiveMediaGuard, { type SensitiveMediaGuardProps } from "@/main/react/sensitive/SensitiveMediaGuard";
import { onSearchResultActivated, type SearchResultAttribution } from "@/main/analytics/searchResultAttribution";

/**
 * Whether the surface rendering these rows is pinned to ONE vertical (`DISCOVERY-15`).
 *
 * The decision lives on the LIST, not on the row, because the list is the only thing that knows it.
 * Every vertical still declares what it is through {@link SearchResultRowBase}'s `typeLabel` — that
 * stays required, so a vertical added later cannot arrive label-less — and this context decides
 * whether saying so out loud carries any information on the surface currently rendering it.
 *
 * On `/search` it carries none: the badge is the leftmost, most-enclosed element of every row, sits
 * directly beneath a tab strip that already names the vertical, and is the only thing on the card
 * that never varies. It is also a plural label applied to a single item. In Spanish it cost a whole
 * result per screen: `SearchResultRowBase` puts the badge and the metadata in one `flex flex-wrap`
 * row, and at 393px "Usuarios" + "Miembro desde hace 3 dias" no longer fits where the English pair
 * does, so every row wrapped and grew a line. Measured card pitch: 99.3 CSS px in English, 125.1 in
 * Spanish (+26%), about 6.1 rows above the fold against 4.8.
 *
 * Defaulting to NOT pinned keeps every existing surface rendering exactly as before, so a list opts
 * IN to the suppression rather than opting out of it. Provided once per list, which is the point:
 * four of the five verticals passed an unconditional badge and one did not, and a per-vertical fix
 * is a fix that can be wired for some verticals and forgotten for others.
 */
export const SearchResultRowVerticalContext = React.createContext<{
    pinnedToSingleVertical: boolean;
    /**
     * SV-138 (FRR `3a`): the search page opts into the phone edge-to-edge shell, so below `sm` its rows
     * leave the column's 16px phone gutter (`-mx-4`), drop the card radius and box border, and keep a
     * single row divider plus their own 16px inner padding, the same shape as a feed row. Page-scoped
     * through this context rather than baked into the row, because `TagPage` renders the same row on a
     * page that keeps the default gutter (decision A29: opt-in, never global).
     */
    edgeToEdgeOnPhone?: boolean;
}>({
    pinnedToSingleVertical: false,
});

/** The phone half of a search row on an edge-to-edge page. Shared with {@link SearchResultSkeleton}. */
export const SEARCH_ROW_EDGE_ON_PHONE_CLASS
    = "max-sm:-mx-4 max-sm:rounded-none max-sm:border-x-0 max-sm:border-t-0 max-sm:border-divider";

export function SearchResultRowBase(props: {
    href?: string | null;
    linkLabel?: string;
    /**
     * What this row IS. Required on every vertical, and rendered as a badge only when the surface has
     * not declared itself pinned to a single vertical — see {@link SearchResultRowVerticalContext}.
     */
    typeLabel: string;
    title: React.ReactNode;
    /**
     * PROSE ONLY — this slot is line-clamped, so anything passed here can be silently cut off.
     * Counts, badges and stat lines belong in {@link stats}, which is not clamped. See the note on
     * the clamp in the body below for what happened when the two shared this slot.
     */
    description?: React.ReactNode;
    /**
     * The row's counts and stat lines, rendered BELOW the clamped description and never clipped.
     *
     * This exists because it must: until 2026-08-02 every vertical passed its excerpt and its stats
     * as one node into `description`, and the `line-clamp-3` on that slot counted the stats as lines
     * of prose. At 412px that deleted the vote/thought counts from every discussion row but one — the
     * survivor being the row whose excerpt happened to wrap to two lines, which is what confirmed the
     * mechanism. Keep prose and counts in separate slots so the clamp can only ever eat prose.
     */
    stats?: React.ReactNode;
    metadata?: React.ReactNode;
    leading?: React.ReactNode;
    trailing?: React.ReactNode;
    className?: string;
    contentGuardProps?: Omit<SensitiveMediaGuardProps, "children">;
    /**
     * Fired from the full-row overlay link only — the row's single navigation path. The search
     * verticals do not use `DiscussionCardDisplayer`, so this is their equivalent of that card's
     * overlay-link seam, and it exists for the same reason: an `onClickCapture` on the row container
     * would also fire for the author link and for the sensitive-content reveal button.
     *
     * Side-effect-only; it must never throw into the navigation it rides on.
     */
    onActivate?: () => void;
    /**
     * Set by every vertical on the search page. Firing the click beacon HERE rather than in each row
     * component is the point: this is the one overlay link all five verticals already share, so the
     * measurement cannot be wired for some verticals and forgotten for others — which is exactly the
     * state the search page was in before 2026-08-01, when only discussions had any click signal.
     *
     * Absent off the search page (the chooser dialogs reuse these rows), which correctly records
     * nothing: there is no query for the click to attribute to.
     */
    searchAttribution?: SearchResultAttribution;
}) {
    const {
        href,
        linkLabel,
        typeLabel,
        title,
        description,
        stats,
        metadata,
        leading,
        trailing,
        className,
        contentGuardProps,
        onActivate,
        searchAttribution,
    } = props;
    const handleActivate = React.useCallback(() => {
        // The row's own side effect first, then the shared search measurement. Both are
        // side-effect-only and neither may throw into the navigation this rides on.
        onActivate?.();
        if (searchAttribution) {
            onSearchResultActivated(searchAttribution);
        }
    }, [onActivate, searchAttribution]);
    const resolvedLinkLabel = linkLabel ?? (typeof title === "string" ? title : undefined);
    const { pinnedToSingleVertical, edgeToEdgeOnPhone } = React.useContext(SearchResultRowVerticalContext);
    const sensitivePreference = React.useMemo(() => getSensitivePreferenceMode(), []);
    const articleContent = (
        <article className="relative flex items-start gap-4 p-4 sm:p-5">
            {leading ? <div className="shrink-0">{leading}</div> : null}

            <div className="min-w-0 flex-1 space-y-2">
                {/* `DISCOVERY-15` — the badge is dropped when the list is already pinned to this
                    vertical. It is not conditional on `typeLabel` being absent: every vertical passes
                    one, and keeping the prop required is what stops a vertical added later from
                    arriving with no label on the surfaces that DO need it. When it is dropped the
                    metadata gets the whole line back, which is the half of the row that carries
                    information. */}
                {!pinnedToSingleVertical || metadata ? (
                    <div className="flex flex-wrap items-center gap-2">
                        {pinnedToSingleVertical ? null : (
                            <Badge variant="outline" className="border-border bg-background2/65">
                                {typeLabel}
                            </Badge>
                        )}
                        {metadata ? <div className="min-w-0 text-xs text-muted-foreground sm:text-sm">{metadata}</div> : null}
                    </div>
                ) : null}

                <div className="min-w-0">
                    {/* `h2`, not `h3` (`DISCOVERY-9`). This row serves exactly two pages, `/search` and
                        `/tag/*`, and on both it is a direct child of the page under its `h1`. As `h3`
                        it skipped a rung: `/tag/*` went `h1` straight to a run of `h3`s, and `/search`
                        had no page heading at all, so the document went from root into twenty `h3`s
                        with nothing naming what page the reader was on. Heading navigation is the
                        primary way screen-reader users orient on a results page; this is the rung it
                        reads. */}
                    <h2 className="line-clamp-2 text-base font-semibold leading-6 text-foreground sm:text-lg">
                        {title}
                    </h2>
                    {/* The clamp stops here on purpose — `stats` is a sibling below, outside it. */}
                    {description ? (
                        <div className="mt-2 line-clamp-3 text-sm leading-6 text-muted-foreground">{description}</div>
                    ) : null}
                </div>

                {stats ? <div className="min-w-0 text-xs text-muted-foreground sm:text-sm">{stats}</div> : null}
            </div>

            {trailing ? <div className="shrink-0">{trailing}</div> : null}
        </article>
    );

    return (
        <div
            className={cn(
                // Design language rules 1-3: a resting row is separated by its hairline (no
                // drop-shadow), sits on the card surface token, uses the card radius token, and takes
                // the one shared hover wash — never a border-colour flip. Focus is communicated by the
                // overlay link's focus ring below, so the border stays constant in every state.
                // Full-strength `border-border`, not `/60`: with the resting shadow removed this
                // hairline is the ONLY thing delimiting one row from the next, and at 60% it measured
                // 1.22:1 against the page — below the 3:1 non-text contrast floor.
                "group relative block overflow-hidden rounded-xl border border-border bg-card card-hover",
                edgeToEdgeOnPhone && SEARCH_ROW_EDGE_ON_PHONE_CLASS,
                className,
            )}
        >
            {href ? (
                <a
                    href={href}
                    aria-label={resolvedLinkLabel}
                    onClick={onActivate || searchAttribution ? handleActivate : undefined}
                    className="absolute inset-0 z-layer-base-front rounded-[inherit] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                />
            ) : null}

            {contentGuardProps ? (
                <SensitiveMediaGuard
                    sensitivePreference={contentGuardProps.hideSensitiveMedia ? sensitivePreference : "show"}
                    {...contentGuardProps}
                >
                    {articleContent}
                </SensitiveMediaGuard>
            ) : articleContent}
        </div>
    );
}

export default SearchResultRowBase;
